import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../../common/Button'
import { getRequest } from '../../../../services/base'
import { useNavigate, useParams } from 'react-router-dom'
import { NavContext } from '../../../../context/global_context'
import { encrypt } from '../../../../utils/encryption'
import LinkWithStatus from '../../../common/LinkWithStatus'
import NoRecordsFound from '../../../common/NoRecordsFound'
import InputText from '../../../common/InputText'
import EllipsisTooltip from '../../../common/EllipsisTooltip'
import { Empty, Flex, Form, List, Popover, Typography, Modal } from 'antd'
import { useIntl } from 'react-intl'
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import RecordsCount from '../../../common/RecordsCount'

const UnMappedSubSystem = () => {
  const { vehicle } = useParams()
  const navigate = useNavigate()
  const unmappedSubSystemRef = useRef()
  const [state, dispatch] = useContext(NavContext)
  const [showUnmapped, setShowUnmapped] = useState(false)
  const [unmappedSubSystemData, setUnmappedSubSystemData] = useState([])
  const [filteredSubSystemData, setFilteredSubSystemData] = useState([])
  const [searchValue, setSearchValue] = useState('')
  const intl = useIntl()
  const [modal, contextHolder] = Modal.useModal()
  const [modalOpen, setModalOpen] = useState(false)

  const getUnmappedFunctionalDiagramList = useCallback(async () => {
    dispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'functionalDiagram.fetchUnmappedSubSystem' },),
        showLoader: true,
      },
      notification: {},
    })
    setShowUnmapped(true)
    let res = await getRequest(
      `/api/sub_system/functional-diagram/list/unmapped/?make=${vehicle}&without_connectors=true`
    )
    if (res.status) {
      setUnmappedSubSystemData(res.data)
      setFilteredSubSystemData(res.data)
      dispatch({
        type: 'loader',
        data: {
          caption: '',
          showLoader: false,
        },
        notification: {},
      })
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'functionalDiagram.unmappedLoadError' }),
        },
      })
    }
  }, [vehicle, dispatch, intl])

  const goToEdit = useCallback(
    (id) => {
      setModalOpen(true)

      modal.confirm({
        icon: <ExclamationCircleOutlined />,
        title: intl.formatMessage({ id: 'functionalDiagram.addSubSystemsConfirmationTitle' }),
        content: intl.formatMessage(
          { id: 'functionalDiagram.addSubSystemsConfirmationContent' },
        ),
        centered: true,
        maskClosable: true,
        onOk: async () => {
          let enc = encrypt(String(id))
          navigate(`/vehicles/${vehicle}/functional-diagram/${enc}`)
          setModalOpen(false)
        },
        onCancel: () => {
          setModalOpen(false)
        }
      })
    },
    [navigate, vehicle, intl, modal]
  )

  const searchSubSystem = useCallback(
    (value) => {
      let tempFilteredData = []
      if (value) {
        tempFilteredData = [...unmappedSubSystemData].filter(
          (row) =>
            row.name.toLowerCase().includes(value.toLowerCase()) ||
            row.sub_system_id.toLowerCase().includes(value.toLowerCase())
        )
      } else {
        tempFilteredData = [...unmappedSubSystemData]
      }
      setFilteredSubSystemData(tempFilteredData)
    },
    [unmappedSubSystemData]
  )

  const SubSystemsList = useMemo(() => {
    return (
      <List
        itemLayout="horizontal"
        dataSource={filteredSubSystemData}
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          )
        }}
        renderItem={(sub_system, index) => (
          <List.Item data-testid="fd-unmapped-item">
            <Flex
              gap={16}
              className="full-width"
              justify="space-between"
              align="center"
            >
              <Flex
                vertical
                gap={0}
                className="full-width"
                style={{ minWidth: 0 }}
              >
                <EllipsisTooltip
                  className="ellipsis"
                  value={`${sub_system.sub_system_id}/${sub_system.name}`}
                >
                  <div className="bolder">{sub_system.sub_system_id}</div>
                </EllipsisTooltip>
                <EllipsisTooltip
                  className="ellipsis text-xs"
                  value={`${sub_system.sub_system_id}/${sub_system.name}`}
                >
                  {sub_system.name}
                </EllipsisTooltip>
              </Flex>

              <LinkWithStatus
                href={`/vehicles/${vehicle}/functional-diagram/${sub_system.id}`}
                title={intl.formatMessage({ id: 'functionalDiagram.addMapping' },)}
                testId="fd-add-mapping"
                onClickFunc={() => {
                  goToEdit(sub_system.id)
                }}
              />
            </Flex>
          </List.Item>
        )}
      />
    )
  }, [filteredSubSystemData, vehicle, goToEdit, intl])

  useEffect(() => {
    if (!showUnmapped) return

    getUnmappedFunctionalDiagramList()
  }, [showUnmapped, getUnmappedFunctionalDiagramList])

  const handlePopoverOpenChange = useCallback(
    (open) => {
      if (modalOpen) return
      setShowUnmapped(open)
    },
    [modalOpen]
  )

  const handleClosePopover = useCallback(() => setShowUnmapped(false), [])

  const handleSearchChange = useCallback(
    (e) => {
      searchSubSystem(e)
      setSearchValue()
    },
    [searchSubSystem]
  )

  return (
    <div ref={unmappedSubSystemRef}>
      <Popover
        open={showUnmapped}
        onOpenChange={handlePopoverOpenChange}
        trigger="click"
        placement="bottomRight"
        classNames={{
          body: 'add-subsystems-popover',
        }}
        title={
          <Flex justify='space-between' align='center'>
            {intl.formatMessage({ id: 'functionalDiagram.addSubSystem' })}
            <Button
              type='text'
              icon={<CloseOutlined />}
              className='ant-popover-close'
              onClick={handleClosePopover}
              aria-label="Close"
            />
          </Flex>
        }
        content={
          <Flex vertical gap={16} style={{ width: 600 }}>
            <Form>
              <InputText
                name="sub_system"
                placeholder={intl.formatMessage({ id: 'functionalDiagram.searchSubSystemPlaceholder' })}
                type="search"
                isRequired={false}
                onChange={handleSearchChange}
                value={searchValue}
              />
            </Form>
            <div className='sub-systems-list'>
              {SubSystemsList}
            </div>
          </Flex>
        }
      >
        <Button type="primary" testId="fd-add-subsystem">
          {intl.formatMessage({ id: 'functionalDiagram.addSubSystem' })}
        </Button>
      </Popover>

      {contextHolder}
    </div>
  )
}

export default UnMappedSubSystem
